import { FormEntryField, Images } from "../../../core/type";
import { Image } from "antd";
import uploadImageSvg from "../../../assets/upload-image.svg";
import { useState } from "react";
import { Button, Carousel, Modal, Select, Space, Tooltip } from "antd";
import { ImageViewer } from "../../../core/component/Upload/ImageViewer";
import { ImageUploaderButton } from "../../../core/component/Upload";
import { DeleteOutlined, LeftCircleFilled, RightCircleFilled } from "@ant-design/icons";
import { translate, confirm } from "../../../core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export const ItemFormImages = ({
  value,
  onChange,
}: FormEntryField<Images[]>) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [priorityOpen, setPriorityOpen] = useState<boolean>(false);
  // Generate priority options
  const generatePriorityOptions = () => {
    const options = [];
    if (value && value.length > 0) {
      for (let i = 0; i < value.length; i++) {
        options.push(
          <Select.Option key={i} value={i}>
            Priority {i + 1}
          </Select.Option>
        );
      }
    }
    return options;
  };

  // Handle priority change for an image
  const handlePriorityChange = (index: number, newPriority: number) => {
    const updatedImages = value ? [...value] : [];

    const duplicateIndex = updatedImages.findIndex(
      (image, i) => image.priority === newPriority && i !== index
    );

    if (duplicateIndex !== -1) {
      updatedImages[duplicateIndex].priority = updatedImages[index].priority;
    }

    updatedImages[index].priority = newPriority;
    // Sort the images by priority
    updatedImages.sort((a: any, b: any) => a.priority - b.priority);

    onChange!(updatedImages);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(value ?? []);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    const reordered = items.map((item, index) => ({
      ...item,
      priority: index + 1,
    }));

    onChange!(reordered);
  };

  return (
    <Space direction="vertical" className="w-100">
      <Space className="d-flex flex-row justify-content-center">
        <ImageUploaderButton
          onUploaded={(image) =>
            onChange!([
              ...(value ?? []),
              { ...image, priority: value?.length ? value?.length + 1 : 0 },
            ])
          }
        />
        <Tooltip title={translate("remove", "current", "image")}>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={async () => {
              const result = await confirm(
                translate('remove_confirmation_message', 'this', 'image', '?')
              )
              if (!result)
                return
              else
                onChange!(value?.filter((e) => e !== value[currentIndex]))
            }}
            size='middle'
          />
        </Tooltip>
      </Space>
      <div>
        {value && value.length > 0 ? (
          <>
            <Image.PreviewGroup>
              <Carousel
                afterChange={setCurrentIndex}
                arrows={true}
                dots={true}
                dotPosition="top"
                effect="fade"
                prevArrow={<LeftCircleFilled />}
                nextArrow={<RightCircleFilled />}
                style={{ width: 300, height: 200 }}
              >
                {value.map(({ filename, priority }, index) => (
                  <div>
                    <div className="bg-dark" key={Date.now()}>
                      <ImageViewer
                        mediaURL={filename}
                        width={300}
                        height={200}
                        mediaType={"image"}
                      />
                    </div>
                    <Button style={{
                      margin: "10px auto",
                      display: "block",
                      bottom: "30px",
                    }} onClick={() => setPriorityOpen(true)}>
                      {translate("set_priority")}
                    </Button>
                  </div>
                ))}
              </Carousel>
            </Image.PreviewGroup>
          </>
        ) : (
          <EmptyImage />
        )}
      </div>
      <Modal
        closable
        open={priorityOpen}
        footer={[]}
        width="40%"
        onCancel={() => setPriorityOpen(false)}
        title={translate("set_priority")}
      >
        <div className="p-1">
          <Carousel
            afterChange={setCurrentIndex}
            arrows={true}
            dots={true}
            dotPosition="top"
            effect="fade"
            prevArrow={<LeftCircleFilled />}
            nextArrow={<RightCircleFilled />}
            style={{ width: 300, height: 200, margin: "0 auto" }}
          >
            {value &&
              value.length > 0 &&
              value.map(({ filename, priority }, index) => (
                <div>
                  <div className="bg-dark" key={Date.now()}>
                    <ImageViewer
                      mediaURL={filename}
                      width={300}
                      height={200}
                      mediaType={"image"}
                    />
                  </div>
                </div>
              ))}
          </Carousel>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="thumbnails" direction="horizontal">
              {(provided: any) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    marginTop: 20,
                    padding: 10,
                    gap: 10,
                    background: "#f5f5f5",
                    borderRadius: 8,
                  }}
                >
                  {value &&
                    value.length > 0 &&
                    value.map(({ filename }, index) => (
                      <Draggable
                        key={filename}
                        draggableId={filename}
                        index={index}
                      >
                        {(provided: any) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              border: "1px solid #d9d9d9",
                              borderRadius: 6,
                              padding: 4,
                              background: "#fff",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <ImageViewer
                              mediaURL={filename}
                              width={80}
                              height={60}
                              mediaType="image"
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </Modal>
    </Space>
  );
};

const EmptyImage = () => (
  <div className="w-100 d-flex flex-row justify-content-center">
    <img
      src={uploadImageSvg}
      alt="upload"
      style={{ width: 300, height: 200 }}
    />
  </div>
);
